import { BAG_COUNTER_KEY, DEFAULT_BAG_COUNTER, DATE_TIME_LOCAL_PATTERN } from '../config/workspaceConfig';

const uid = prefix => `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
const isDateTimeLocal = value => typeof value === 'string' && DATE_TIME_LOCAL_PATTERN.test(value);
const isValidDateOnly = value => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};
const isValidDateTimeLocal = value => {
  if (!isDateTimeLocal(value)) return false;
  const [datePart, timePart] = value.split('T');
  if (!isValidDateOnly(datePart)) return false;
  const [hours, minutes, seconds = '0'] = timePart.split(':').map(Number);
  return hours <= 23 && minutes <= 59 && seconds <= 59;
};
const normalizePhoneNumber = value => value.replace(/[\s().-]/g, '');
const isValidPhoneNumber = value => {
  const normalized = normalizePhoneNumber(value);
  return /^\+?\d+$/.test(normalized) && normalized.replace(/\D/g, '').length <= 15;
};
const dateTime = (hoursAgo = 0) => {
  const date = new Date(Date.now() - hoursAgo * 3600000);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};
const dateOnly = (daysFromNow = 0) => {
  const date = new Date(Date.now() + daysFromNow * 86400000);
  return date.toISOString().slice(0, 10);
};

function idLabel(db, module, id) {
  const item = (db[module] || []).find((record) => record.id === id);
  if (!item) return "—";
  return (
    item.name ||
    item.airportName ||
    item.beltId ||
    item.code ||
    item.sensorId ||
    item.plcId ||
    item.bearingId ||
    item.deviceId ||
    item.radioId ||
    item.displayId ||
    item.isoCode ||
    "Unnamed"
  );
}

function airportCode(db, airportId) {
  const entry = db.iataCodes.find((item) => item.airportId === airportId);
  return entry?.code || "—";
}

function rowValue(db, item, column) {
  const [key, , relation] = column;
  if (relation === "iata") return airportCode(db, item.id);
  if (relation === "airportCountry")
    return idLabel(
      db,
      "countries",
      db.airports.find((airport) => airport.id === item.airportId)?.countryId,
    );
  if (relation === "airportState")
    return idLabel(
      db,
      "states",
      db.airports.find((airport) => airport.id === item.airportId)?.stateId,
    );
  if (relation === "airportCity")
    return (
      db.airports.find((airport) => airport.id === item.airportId)?.city || "—"
    );
  if (relation === "beltCode") return idLabel(db, "belts", item.beltId);
  if (relation === "beltSensor") {
    const sensor = db.sensors.find((entry) => entry.beltId === item.id);
    return sensor ? sensor.sensorId : "Not mapped";
  }
  if (relation === "beltPlc") {
    const plc = db.plcs.find((entry) => entry.beltId === item.id);
    return plc ? plc.plcId : "Not mapped";
  }
  if (relation === "beltName")
    return db.belts.find((belt) => belt.id === item.beltId)?.name || "—";
  if (relation === "beltAirport")
    return idLabel(
      db,
      "airports",
      db.belts.find((belt) => belt.id === item.beltId)?.airportId,
    );
  if (relation === "beltState")
    return idLabel(
      db,
      "states",
      db.belts.find((belt) => belt.id === item.beltId)?.stateId,
    );
  if (relation) return idLabel(db, relation, item[key]);
  if (key === "mobileNumber")
    return item.mobileNumber
      ? `••••••${item.mobileNumber.slice(-4)}`
      : "Not provided";
  if (
    key === "runningStatus" ||
    key === "connectionStatus" ||
    key === "faultStatus" ||
    key === "condition" ||
    key === "deviceStatus" ||
    key === "status" ||
    key === "systemStatus"
  )
    return item[key] || "—";
  if (key === "currentCount" || key === "totalCount")
    return Number(item[key] || 0).toLocaleString();
  return item[key] === "" || item[key] === undefined || item[key] === null
    ? "—"
    : String(item[key]);
}

function moduleRecordLabel(module, item) {
  return (
    item.name ||
    item.airportName ||
    item.beltId ||
    item.code ||
    item.sensorId ||
    item.plcId ||
    item.bearingId ||
    item.deviceId ||
    item.radioId ||
    item.displayId ||
    item.isoCode ||
    module.label
  );
}

function recordStatus(module, item) {
  if (module.key === "beltRunning") return item.runningStatus || "";
  if (module.key === "sensors")
    return item.connectionStatus || item.faultStatus || "";
  if (
    module.key === "plcs" ||
    module.key === "cellPhones" ||
    module.key === "displays"
  )
    return item.connectionStatus || "";
  if (module.key === "bearings") return item.condition || "";
  if (module.key === "radios") return item.deviceStatus || "";
  if (module.key === "systems") return item.systemStatus || "";
  return item.status || item.runningStatus || item.connectionStatus || "";
}

function formatTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function loadBagCounter() {
  try {
    const saved = window.localStorage.getItem(BAG_COUNTER_KEY);
    if (!saved) return DEFAULT_BAG_COUNTER;
    const parsed = JSON.parse(saved);
    if (
      !Number.isSafeInteger(parsed.target) ||
      parsed.target < 1 ||
      !Number.isSafeInteger(parsed.durationMinutes) ||
      parsed.durationMinutes < 1 ||
      !Number.isSafeInteger(parsed.durationMinutes * 60 * 1000) ||
      (parsed.startedAt !== null && !Number.isFinite(parsed.startedAt))
    ) {
      throw new Error("Saved bag counter settings are invalid.");
    }
    return parsed;
  } catch (error) {
    console.error("Unable to load live bag counter settings.", error);
    return DEFAULT_BAG_COUNTER;
  }
}

function formatRemainingTime(milliseconds) {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds]
    .map((value) => String(value).padStart(2, "0"))
    .join(":");
}


export {
  airportCode,
  dateOnly,
  dateTime,
  formatRemainingTime,
  formatTime,
  idLabel,
  isDateTimeLocal,
  isValidDateOnly,
  isValidDateTimeLocal,
  isValidPhoneNumber,
  loadBagCounter,
  moduleRecordLabel,
  normalizePhoneNumber,
  recordStatus,
  rowValue,
  uid,
};
